/**
 * Error Drawer component for displaying error history.
 * Shows a slide panel with all captured API errors and their details.
 *
 * Features:
 * - Lists errors newest-first with timestamps
 * - Shows error details including requestId, traceId
 * - Copy-to-clipboard functionality
 * - Individual error dismissal
 * - Clear all errors
 *
 * @module components/ui/ErrorDrawer
 */

import { useCallback } from 'react';
import { X, Copy, Trash2, AlertTriangle, CheckCircle, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import { useErrorStore, formatErrorForClipboard, type AppError } from '@/stores/useErrorStore';
import { SlidePanel } from './SlidePanel';
import { Button } from './Button';

/**
 * Format a relative time string from an ISO timestamp
 */
function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return 'Just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  } else {
    return date.toLocaleDateString();
  }
}

/**
 * Props for ErrorCard component
 */
interface ErrorCardProps {
  error: AppError;
  onDismiss: (id: string) => void;
  onCopy: (error: AppError) => void;
}

/**
 * Individual error card within the drawer
 */
function ErrorCard({ error, onDismiss, onCopy }: ErrorCardProps) {
  return (
    <div className="bg-gray-50 dark:bg-[#1a1a1a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg p-4 border-l-4 border-l-red-500">
      {/* Header row with timestamp and actions */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <span>{formatRelativeTime(error.timestamp)}</span>
          {error.status && (
            <span className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded font-medium">
              {error.status}
            </span>
          )}
          {error.errorCode !== undefined && (
            <span className="px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 rounded font-medium">
              Code: {error.errorCode}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onCopy(error)}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#2a2a2a] rounded transition-colors"
            title="Copy error details"
            aria-label="Copy error details"
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => onDismiss(error.id)}
            className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-gray-200 dark:hover:bg-[#2a2a2a] rounded transition-colors"
            title="Dismiss error"
            aria-label="Dismiss error"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Error message */}
      <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">{error.message}</p>

      {/* Detail (if present) */}
      {error.detail && error.detail !== error.message && (
        <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">{error.detail}</p>
      )}

      {/* IDs section */}
      {(error.requestId || error.traceId) && (
        <div className="space-y-1 pt-2 border-t border-gray-200 dark:border-[#2a2a2a]">
          {error.requestId && (
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 w-16">
                Request
              </span>
              <code className="text-[11px] font-mono text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-[#252525] px-1.5 py-0.5 rounded truncate flex-1">
                {error.requestId}
              </code>
            </div>
          )}
          {error.traceId && (
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 w-16">
                Trace
              </span>
              <code className="text-[11px] font-mono text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-[#252525] px-1.5 py-0.5 rounded truncate flex-1">
                {error.traceId}
              </code>
              <a
                href={`/logs/traces/${error.traceId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/50 rounded transition-colors"
                title="Debug this trace in a new tab"
              >
                <ExternalLink className="h-3 w-3" />
                Debug
              </a>
            </div>
          )}
          {error.url && (
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 w-16">
                URL
              </span>
              <code className="text-[11px] font-mono text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-[#252525] px-1.5 py-0.5 rounded truncate flex-1">
                {error.url}
              </code>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Empty state when no errors exist
 */
function EmptyErrorState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
        <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
      </div>
      <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">No errors</h3>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        API errors will appear here when they occur.
      </p>
    </div>
  );
}

/**
 * ErrorDrawer - Slide panel showing error history
 */
export function ErrorDrawer() {
  const { errors, isDrawerOpen, closeDrawer, removeError, clearAllErrors } = useErrorStore();

  const handleCopy = useCallback(async (error: AppError) => {
    const text = formatErrorForClipboard(error);
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Error details copied to clipboard', { duration: 2000 });
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      toast.error('Failed to copy to clipboard');
    }
  }, []);

  const handleDismiss = useCallback(
    (id: string) => {
      removeError(id);
    },
    [removeError]
  );

  const handleClearAll = useCallback(() => {
    clearAllErrors();
    toast.success('All errors cleared', { duration: 2000 });
  }, [clearAllErrors]);

  return (
    <SlidePanel
      isOpen={isDrawerOpen}
      onClose={closeDrawer}
      title="Error History"
      description={
        errors.length > 0 ? `${errors.length} error${errors.length === 1 ? '' : 's'}` : undefined
      }
      size="md"
      position="right"
      footer={
        errors.length > 0 ? (
          <Button variant="ghost" size="sm" onClick={handleClearAll} className="text-red-600">
            <Trash2 className="h-4 w-4 mr-2" />
            Clear All
          </Button>
        ) : undefined
      }
    >
      {errors.length === 0 ? (
        <EmptyErrorState />
      ) : (
        <div className="space-y-3">
          {/* Info banner */}
          <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-lg text-xs text-blue-700 dark:text-blue-300">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <p>
              Click <Copy className="h-3 w-3 inline mx-0.5" /> to copy error details including
              Request ID and Trace ID for sharing with support.
            </p>
          </div>

          {/* Error list */}
          {errors.map((error) => (
            <ErrorCard key={error.id} error={error} onDismiss={handleDismiss} onCopy={handleCopy} />
          ))}
        </div>
      )}
    </SlidePanel>
  );
}

export type { AppError };
